import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUsers, FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaSave, FaSpinner, FaFileExcel
} from 'react-icons/fa';
import empleadosApi from '../../services/api/empleadosApi';
import CargaMasivaEmpleados from './CargaMasivaEmpleados';
import useRrhhProyecto from '../../hooks/useRrhhProyecto';
import RrhhProyectoSelector from '../../components/rrhh/RrhhProyectoSelector';

// ── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  tipo_documento_identidad_id: '',
  numero_documento_identidad: '',
  nombre_1: '',
  nombre_2: '',
  apellido_1: '',
  apellido_2: '',
  genero_id: '',
  fecha_nacimiento: '',
  proyecto_id: '',
  regimen_empleado_id: '',
  direccion: '',
  telefono: '',
  correo_electronico: '',
  fecha_inicio_labores: '',
  cargo_descripcion: '',
  banco_id: '',
  numero_cuenta: '',
  cci: '',
  fondo_pensiones: '',
  spp_id: '',
  cussp: '',
  tipo_comision_id: '',
  estado_empleado_id: '',
  activo: 1,
};

// ── Toast minimalista ─────────────────────────────────────────────────────────

const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const colors = { success: '#11998e', error: '#ef4444', warning: '#f59e0b' };
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      background: colors[type] || '#667eea', color: '#fff',
      padding: '12px 20px', borderRadius: 10,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 10, maxWidth: 380
    }}>
      <span style={{ flex: 1 }}>{msg}</span>
      <FaTimes style={{ cursor: 'pointer', opacity: 0.8 }} onClick={onClose} />
    </div>
  );
};

// ── Modal de Empleado ─────────────────────────────────────────────────────────

const EmpleadoModal = ({
  show, onClose, onSaved,
  empleado,        // null → crear, objeto → editar
  tiposDocs, generos, regimenes, estados, bancos, spps, tiposComision,
  proyectoId
}) => {
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});
  const [step, setStep]       = useState(1);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    if (empleado) {
      setForm({
        tipo_documento_identidad_id: empleado.tipo_documento_identidad_id || '',
        numero_documento_identidad:  empleado.numero_documento_identidad  || '',
        nombre_1:   empleado.nombre_1   || '',
        nombre_2:   empleado.nombre_2   || '',
        apellido_1: empleado.apellido_1 || '',
        apellido_2: empleado.apellido_2 || '',
        genero_id:  empleado.genero_id  || '',
        fecha_nacimiento: empleado.fecha_nacimiento ? String(empleado.fecha_nacimiento).slice(0, 10) : '',
        proyecto_id: empleado.proyecto_id || proyectoId,
        regimen_empleado_id: empleado.regimen_empleado_id || '',
        direccion:  empleado.direccion  || '',
        telefono:   empleado.telefono   || '',
        correo_electronico: empleado.correo_electronico || '',
        fecha_inicio_labores: empleado.fecha_inicio_labores
          ? String(empleado.fecha_inicio_labores).slice(0, 10) : '',
        cargo_descripcion: empleado.cargo_descripcion || '',
        banco_id: empleado.banco_id || '',
        numero_cuenta: empleado.numero_cuenta || '',
        cci: empleado.cci || '',
        fondo_pensiones: empleado.fondo_pensiones || '',
        spp_id: empleado.spp_id || '',
        cussp: empleado.cussp || '',
        tipo_comision_id: empleado.tipo_comision_id || '',
        estado_empleado_id: empleado.estado_empleado_id || '',
        activo: empleado.activo ?? 1,
      });
    } else {
      setForm({ ...EMPTY_FORM, proyecto_id: proyectoId || '' });
    }
    setErrors({});
  }, [show, empleado, proyectoId]);

  const validate = () => {
    const e = {};
    if (!form.tipo_documento_identidad_id) e.tipo_documento_identidad_id = 'Requerido';
    if (!form.numero_documento_identidad.trim()) e.numero_documento_identidad = 'Requerido';
    if (!form.nombre_1.trim())   e.nombre_1   = 'Requerido';
    if (!form.apellido_1.trim()) e.apellido_1 = 'Requerido';
    if (!form.genero_id)  e.genero_id  = 'Requerido';
    if (!form.regimen_empleado_id) e.regimen_empleado_id = 'Requerido';
    if (!form.estado_empleado_id)  e.estado_empleado_id  = 'Requerido';
    if (form.correo_electronico && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo_electronico)) {
      e.correo_electronico = 'Correo no válido';
    }
    return e;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.banco_id) e.banco_id = 'Requerido';
    if (!form.numero_cuenta?.trim()) e.numero_cuenta = 'Requerido';
    if (!form.cci?.trim()) e.cci = 'Requerido';
    if (!form.fondo_pensiones) e.fondo_pensiones = 'Requerido';
    if (form.fondo_pensiones === 'AFP') {
      if (!form.tipo_comision_id) e.tipo_comision_id = 'Requerido';
    }
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => { const n = { ...er }; delete n[name]; return n; });
  };

  const handleNext = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      handleNext();
      return;
    }
    
    const errs2 = validateStep2();
    if (Object.keys(errs2).length) { setErrors(errs2); return; }
    
    setSaving(true);
    try {
      const formatText = (val) => val ? val.trim().toUpperCase() : '';
      
      const payload = {
        ...form,
        nombre_1: formatText(form.nombre_1),
        nombre_2: formatText(form.nombre_2),
        apellido_1: formatText(form.apellido_1),
        apellido_2: formatText(form.apellido_2),
        cargo_descripcion: formatText(form.cargo_descripcion),
        direccion: formatText(form.direccion),
        tipo_documento_identidad_id: Number(form.tipo_documento_identidad_id),
        genero_id:  Number(form.genero_id),
        proyecto_id: Number(form.proyecto_id),
        regimen_empleado_id: Number(form.regimen_empleado_id),
        estado_empleado_id:  Number(form.estado_empleado_id),
        activo: Number(form.activo),
        fecha_nacimiento: form.fecha_nacimiento || null,
        fecha_inicio_labores: form.fecha_inicio_labores || null,
        banco_id: form.banco_id ? Number(form.banco_id) : null,
        spp_id: form.spp_id ? Number(form.spp_id) : null,
        tipo_comision_id: form.tipo_comision_id ? Number(form.tipo_comision_id) : null,
      };
      if (empleado?.id) {
        await empleadosApi.updateEmpleado(empleado.id, payload);
      } else {
        await empleadosApi.createEmpleado(payload);
      }
      onSaved(empleado ? 'actualizado' : 'creado');
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || 'Error al guardar empleado';
      setErrors({ _global: detail });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const fld = (label, name, type = 'text', required = false) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600,
        color: '#374151', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <input
        type={type} name={name} value={form[name]}
        onChange={handleChange}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14,
          border: `1.5px solid ${errors[name] ? '#ef4444' : '#d1d5db'}`,
          outline: 'none', boxSizing: 'border-box',
        }}
      />
      {errors[name] && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors[name]}</span>}
    </div>
  );

  const sel = (label, name, options, valueKey = 'id', labelKey = 'nombre', required = false) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600,
        color: '#374151', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <select
        name={name} value={form[name]} onChange={handleChange}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14,
          border: `1.5px solid ${errors[name] ? '#ef4444' : '#d1d5db'}`,
          background: '#fff', outline: 'none', boxSizing: 'border-box',
        }}
      >
        <option value="">— Seleccionar —</option>
        {options.map(o => (
          <option key={o[valueKey]} value={o[valueKey]}>
            {typeof labelKey === 'function' ? labelKey(o) : o[labelKey]}
          </option>
        ))}
      </select>
      {errors[name] && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors[name]}</span>}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '92%', maxWidth: 760,
        maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '16px 16px 0 0', padding: '18px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <h5 style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: 18 }}>
            <FaUsers style={{ marginRight: 8 }} />
            {empleado ? 'Editar Empleado' : 'Nuevo Empleado'} - {step === 1 ? 'Datos Generales' : 'Datos Bancarios'}
          </h5>
          <FaTimes onClick={onClose} style={{ color: '#fff', cursor: 'pointer', fontSize: 18 }} />
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px 28px' }}>
          {errors._global && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16,
              color: '#dc2626', fontSize: 14 }}>
              {errors._global}
            </div>
          )}

          {step === 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              {/* Col 1 */}
              <div>
                <p style={{ fontWeight: 700, color: '#667eea', fontSize: 13,
                  borderBottom: '2px solid #667eea', paddingBottom: 4, marginBottom: 14 }}>
                  Identificación
                </p>
                {sel('Tipo de Documento', 'tipo_documento_identidad_id', tiposDocs, 'id', 'descripcion', true)}
                {fld('Número de Documento', 'numero_documento_identidad', 'text', true)}
                {sel('Género', 'genero_id', generos, 'id', 'nombre', true)}
                {fld('Fecha de Nacimiento', 'fecha_nacimiento', 'date')}

                <p style={{ fontWeight: 700, color: '#667eea', fontSize: 13,
                  borderBottom: '2px solid #667eea', paddingBottom: 4, marginBottom: 14, marginTop: 8 }}>
                  Nombres y Apellidos
                </p>
                {fld('Primer Nombre', 'nombre_1', 'text', true)}
                {fld('Segundo Nombre', 'nombre_2')}
                {fld('Primer Apellido', 'apellido_1', 'text', true)}
                {fld('Segundo Apellido', 'apellido_2')}
              </div>

              {/* Col 2 */}
              <div>
                <p style={{ fontWeight: 700, color: '#667eea', fontSize: 13,
                  borderBottom: '2px solid #667eea', paddingBottom: 4, marginBottom: 14 }}>
                  Datos Laborales
                </p>
                {sel('Régimen Laboral', 'regimen_empleado_id', regimenes, 'id', o => `${o.codigo} — ${o.descripcion}`, true)}
                {fld('Fecha Inicio Labores', 'fecha_inicio_labores', 'date')}
                {fld('Cargo', 'cargo_descripcion', 'text')}
                {sel('Estado', 'estado_empleado_id', estados, 'id', 'nombre', true)}
                {sel('Activo', 'activo', [{ id: 1, nombre: 'Sí' }, { id: 0, nombre: 'No' }], 'id', 'nombre')}

                <p style={{ fontWeight: 700, color: '#667eea', fontSize: 13,
                  borderBottom: '2px solid #667eea', paddingBottom: 4, marginBottom: 14, marginTop: 8 }}>
                  Datos de Contacto
                </p>
                {fld('Teléfono', 'telefono')}
                {fld('Dirección', 'direccion')}
                {fld('Correo electrónico', 'correo_electronico', 'email')}
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <div>
                <p style={{ fontWeight: 700, color: '#667eea', fontSize: 13,
                  borderBottom: '2px solid #667eea', paddingBottom: 4, marginBottom: 14 }}>
                  Banco
                </p>
                {sel('Banco', 'banco_id', bancos, 'id', 'nombre', true)}
                {fld('N° Cuenta', 'numero_cuenta', 'text', true)}
                {fld('CCI', 'cci', 'text', true)}
              </div>
              <div>
                <p style={{ fontWeight: 700, color: '#667eea', fontSize: 13,
                  borderBottom: '2px solid #667eea', paddingBottom: 4, marginBottom: 14 }}>
                  Sistema de Pensiones
                </p>
                {sel('Fondo', 'fondo_pensiones', [{ id: 'AFP', nombre: 'AFP' }, { id: 'ONP', nombre: 'ONP' }], 'id', 'nombre', true)}
                
                {form.fondo_pensiones === 'AFP' && (
                  <>
                    {sel('AFP', 'spp_id', spps, 'id', 'nombre', false)}
                    {fld('CUSSP', 'cussp')}
                    {sel('Tipo comisión', 'tipo_comision_id', tiposComision, 'id', 'nombre', true)}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12,
            marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            {step === 1 ? (
              <>
                <button type="button" onClick={onClose}
                  style={{ padding: '10px 22px', borderRadius: 8, border: '1.5px solid #d1d5db',
                    background: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                  Cancelar
                </button>
                <button type="submit"
                  style={{
                    padding: '10px 22px', borderRadius: 8, border: 'none',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: '#fff', fontWeight: 700, cursor: 'pointer',
                    fontSize: 14, display: 'flex', alignItems: 'center', gap: 8
                  }}>
                  Siguiente
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setStep(1)} disabled={saving}
                  style={{ padding: '10px 22px', borderRadius: 8, border: '1.5px solid #d1d5db',
                    background: '#fff', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                  Atrás
                </button>
                <button type="submit" disabled={saving}
                  style={{
                    padding: '10px 22px', borderRadius: 8, border: 'none',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
                    opacity: saving ? 0.8 : 1,
                  }}>
                  {saving ? <FaSpinner className="fa-spin" /> : <FaSave />}
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal de Confirmación de Eliminación ──────────────────────────────────────

const ConfirmModal = ({ show, empleado, onConfirm, onCancel, loading }) => {
  if (!show || !empleado) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2100,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%',
        boxShadow: '0 16px 48px rgba(0,0,0,0.25)', textAlign: 'center'
      }}>
        <div style={{
          width: 60, height: 60, borderRadius: '50%',
          background: '#fef2f2', margin: '0 auto 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <FaTrash style={{ color: '#ef4444', fontSize: 24 }} />
        </div>
        <h5 style={{ fontWeight: 700, marginBottom: 8 }}>¿Eliminar empleado?</h5>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
          Se eliminará permanentemente a{' '}
          <strong>{empleado.nombre_completo || `${empleado.apellido_1}, ${empleado.nombre_1}`}</strong>.
          Esta acción no se puede deshacer.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={onCancel}
            style={{ padding: '10px 22px', borderRadius: 8,
              border: '1.5px solid #d1d5db', background: '#fff',
              fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ padding: '10px 22px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#fff', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, opacity: loading ? 0.8 : 1 }}>
            {loading ? <FaSpinner className="fa-spin" /> : <FaTrash />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Componente Principal ──────────────────────────────────────────────────────

const Empleados = () => {
  const {
    isCorporativo,
    proyectoId,
    setProyectoId,
    proyectos,
    loadingProyectos,
    needsProyectoSelection,
  } = useRrhhProyecto();

  // Datos de tabla
  const [empleados,  setEmpleados]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');

  // Catálogos
  const [tiposDocs,  setTiposDocs]  = useState([]);
  const [generos,    setGeneros]    = useState([]);
  const [regimenes,  setRegimenes]  = useState([]);
  const [estados,    setEstados]    = useState([]);
  const [bancos,     setBancos]     = useState([]);
  const [spps,       setSpps]       = useState([]);
  const [tiposComision, setTiposComision] = useState([]);

  // Modales
  const [showModal,    setShowModal]    = useState(false);
  const [editEmpleado, setEditEmpleado] = useState(null);   // null = crear
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [toDelete,     setToDelete]     = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [showCargaMasiva, setShowCargaMasiva] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => setToast({ msg, type });

  // ── Cargar empleados ─────────────────────────────────────────────────────

  const fetchEmpleados = useCallback(async (q = search) => {
    if (!proyectoId) {
      setEmpleados([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await empleadosApi.getEmpleados(proyectoId, q);
      setEmpleados(res.data || []);
    } catch {
      showToast('Error al cargar la lista de empleados', 'error');
    } finally {
      setLoading(false);
    }
  }, [proyectoId, search]);

  // Cargar catálogos al montar
  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        const [tdRes, gRes, regsRes, estsRes, bancRes] = await Promise.all([
          empleadosApi.getTiposDocumentos(),
          empleadosApi.getGeneros(),
          empleadosApi.getRegimenes(),
          empleadosApi.getEstados(),
          empleadosApi.getCatalogosBancarios(),
        ]);
        setTiposDocs(tdRes.data  || []);
        setGeneros(gRes.data     || []);
        setRegimenes(regsRes.data || []);
        setEstados(estsRes.data   || []);
        if (bancRes.data) {
          setBancos(bancRes.data.bancos || []);
          setSpps(bancRes.data.spp || []);
          setTiposComision(bancRes.data.tipos_comision || []);
        }
      } catch (err) {
        console.error('Error cargando catálogos:', err);
      }
    };

    loadCatalogos();
    fetchEmpleados('');
  }, [proyectoId]); // eslint-disable-line



  // Búsqueda con debounce
  useEffect(() => {
    const t = setTimeout(() => fetchEmpleados(search), 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  // ── Acciones ─────────────────────────────────────────────────────────────

  const openCreate = () => { setEditEmpleado(null); setShowModal(true); };
  const openEdit   = (emp) => { setEditEmpleado(emp); setShowModal(true); };

  const handleSaved = (action) => {
    setShowModal(false);
    showToast(`Empleado ${action} correctamente`);
    fetchEmpleados(search);
  };

  const openConfirmDelete = (emp) => { setToDelete(emp); setShowConfirm(true); };
  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await empleadosApi.deleteEmpleado(toDelete.id);
      showToast('Empleado eliminado correctamente');
      setShowConfirm(false);
      setToDelete(null);
      fetchEmpleados(search);
    } catch {
      showToast('Error al eliminar el empleado', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        .dh-carga-bg { background: linear-gradient(135deg,#f8f9fa,#e9ecef); min-height:100vh; padding: 2rem; font-family: 'Inter', sans-serif; }
        .dh-card-main { border:none; border-radius:20px; box-shadow:0 15px 35px rgba(0,0,0,.1); background:#fff; overflow:hidden; }
        .dh-card-header { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; padding:1.5rem 2rem; border:none; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .dh-header-title { color:#fff; font-weight:700; font-size:1.5rem; margin:0; display:flex; align-items:center; gap:12px; }
        .btn-dh-primary { background:linear-gradient(135deg,#11998e,#38ef7d); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(17,153,142,.3); transition:all .3s; cursor:pointer; display:flex; align-items:center; gap:8px; }
        .btn-dh-primary:hover { background:linear-gradient(135deg,#38ef7d,#2dd4bf); transform:translateY(-2px); color:#fff; }
        .btn-dh-warning { background:linear-gradient(135deg,#f093fb,#f5576c); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(240,147,251,.3); transition:all .3s; cursor:pointer; display:flex; align-items:center; gap:8px; }
        .btn-dh-warning:hover { background:linear-gradient(135deg,#f5576c,#e91e63); transform:translateY(-2px); color:#fff; }
      `}</style>

      <div className="dh-carga-bg">
        {toast && (
          <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />
        )}

        <RrhhProyectoSelector
          isCorporativo={isCorporativo}
          proyectos={proyectos}
          proyectoId={proyectoId}
          onChange={setProyectoId}
          loading={loadingProyectos}
        />

        {needsProyectoSelection && (
          <div className="dh-alert dh-alert-info mb-3" style={{ borderRadius: 10 }}>
            Seleccione un proyecto para ver y gestionar empleados.
          </div>
        )}

        {/* Tarjeta principal */}
        <div className="dh-card-main">

          {/* Header */}
          <div className="dh-card-header">
            <h5 className="dh-header-title">
              <FaUsers size={28} />
              Empleados
            </h5>
            <div className="btn-group gap-2">
              <button
                onClick={() => setShowCargaMasiva(true)}
                className="btn-dh-warning"
              >
                <FaFileExcel /> Carga Masiva
              </button>
              <button
                onClick={openCreate}
                className="btn-dh-primary"
              >
                <FaPlus /> Nuevo Empleado
              </button>
            </div>
          </div>

          {/* Buscador */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 440 }}>
            <FaSearch style={{ position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }} />
            <input
              type="text"
              placeholder="Buscar por nombre o número de documento…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px 10px 40px',
                borderRadius: 10, border: '1.5px solid #e5e7eb',
                fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
            {search && (
              <FaTimes
                onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 14, top: '50%',
                  transform: 'translateY(-50%)', color: '#9ca3af',
                  cursor: 'pointer', fontSize: 13 }}
              />
            )}
          </div>
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            {loading ? 'Cargando…' : `${empleados.length} empleado${empleados.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Tabla */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                {['#', 'Nombre Completo', 'Documento', 'Régimen', 'Teléfono', 'Inicio Labores', 'Banco', 'Pensión', 'Estado', 'Activo', 'Acciones']
                  .map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left',
                      fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', fontSize: 13 }}>
                      {h}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <FaSpinner className="fa-spin" style={{ fontSize: 28, color: '#667eea' }} />
                    <p style={{ color: '#9ca3af', marginTop: 8 }}>Cargando empleados…</p>
                  </td>
                </tr>
              ) : empleados.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 60 }}>
                    <FaUsers size={48} style={{ color: '#d1d5db', marginBottom: 12 }} />
                    <p style={{ color: '#9ca3af', margin: 0, fontWeight: 500 }}>
                      {search ? 'No se encontraron empleados con esa búsqueda' : 'No hay empleados registrados en este proyecto'}
                    </p>
                    {!search && (
                      <button onClick={openCreate}
                        style={{ marginTop: 16, background: 'linear-gradient(135deg,#667eea,#764ba2)',
                          color:'#fff', border:'none', borderRadius:8, padding:'8px 18px',
                          fontWeight:700, cursor:'pointer', fontSize:13 }}>
                        <FaPlus style={{ marginRight:6 }} />Agregar el primer empleado
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                empleados.map((emp, idx) => (
                  <tr key={emp.id}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa',
                      transition: 'background .15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}
                  >
                    <td style={{ padding: '11px 16px', color: '#9ca3af', fontWeight: 600 }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: '11px 16px', fontWeight: 600, color: '#111827' }}>
                      {emp.nombre_completo ||
                        `${emp.apellido_1}${emp.apellido_2 ? ' ' + emp.apellido_2 : ''}, ${emp.nombre_1}${emp.nombre_2 ? ' ' + emp.nombre_2 : ''}`}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#374151' }}>
                      <span style={{ fontSize: 11, color: '#9ca3af', display: 'block' }}>
                        {emp.tipo_documento_identidad}
                      </span>
                      {emp.numero_documento_identidad}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        background: '#ede9fe', color: '#7c3aed',
                        borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700
                      }}>
                        {emp.regimen_empleado || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', color: '#374151' }}>
                      {emp.telefono || <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#374151' }}>
                      {emp.fecha_inicio_labores
                        ? String(emp.fecha_inicio_labores).slice(0, 10)
                        : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#374151' }}>
                      {emp.banco || <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#374151' }}>
                      {emp.fondo_pensiones ? (
                        <span style={{
                          background: '#f3f4f6', color: '#374151',
                          borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700
                        }}>
                          {emp.fondo_pensiones === 'AFP' ? (emp.sistema_privado_pensiones_codigo || 'AFP') : 'ONP'}
                        </span>
                      ) : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        background: emp.estado_empleado === 'Activo' ? '#d1fae5' : '#fee2e2',
                        color:      emp.estado_empleado === 'Activo' ? '#065f46' : '#991b1b',
                        borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700
                      }}>
                        {emp.estado_empleado || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        background: emp.activo ? '#d1fae5' : '#f3f4f6',
                        color:      emp.activo ? '#065f46' : '#6b7280',
                        borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700
                      }}>
                        {emp.activo ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(emp)}
                          title="Editar"
                          style={{
                            background: 'linear-gradient(135deg,#667eea,#764ba2)',
                            color:'#fff', border:'none', borderRadius:7,
                            padding:'6px 12px', cursor:'pointer', fontSize:13,
                            display:'flex', alignItems:'center', gap:5
                          }}>
                          <FaEdit /> Editar
                        </button>
                        <button onClick={() => openConfirmDelete(emp)}
                          title="Eliminar"
                          style={{
                            background:'linear-gradient(135deg,#ef4444,#dc2626)',
                            color:'#fff', border:'none', borderRadius:7,
                            padding:'6px 12px', cursor:'pointer', fontSize:13,
                            display:'flex', alignItems:'center', gap:5
                          }}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modales */}
      <EmpleadoModal
        show={showModal} onClose={() => setShowModal(false)}
        empleado={editEmpleado} onSaved={handleSaved}
        tiposDocs={tiposDocs} generos={generos}
        regimenes={regimenes} estados={estados}
        bancos={bancos} spps={spps} tiposComision={tiposComision}
        proyectoId={proyectoId}
      />
      <ConfirmModal
        show={showConfirm}
        empleado={toDelete}
        onConfirm={handleDelete}
        onCancel={() => { setShowConfirm(false); setToDelete(null); }}
        loading={deleting}
      />
      
      {/* MODAL CARGA MASIVA */}
      <CargaMasivaEmpleados
        show={showCargaMasiva}
        onClose={() => setShowCargaMasiva(false)}
        onSaved={() => fetchEmpleados(search)}
        proyectoId={proyectoId}
      />

      </div>
    </>
  );
};

export default Empleados;
